import React, { useState, useCallback, useMemo, useRef } from 'react';
import { emitVisitCompleteWhenPhotosSettle, emitVisitError } from '../lib/embedBridge';
import { useHandoffSeed, skipHandoffContact } from '@/lib/handoff';
import { usePreview, PreviewInterstitial } from './preview/PreviewShell';
import LabFlowIntent from './intake/LabFlowIntent';
import LabPanelOverview, { initialPanelAnswers } from './intake/LabPanelOverview';
import { resolveRequisitionNeed, requisitionCompletionCopy } from '../data/labRequisition';
import QuestionStep from './intake/QuestionStep';
import BasicContactStep from './intake/BasicContactStep';
import ShippingAddressStep from './intake/ShippingAddressStep';
import ConsentDropdownStep from './intake/ConsentDropdownStep';
import DisqualifiedStep from './intake/DisqualifiedStep';
import FileUploadStep from './intake/FileUploadStep';
import Footer from './intake/Footer';
import SquarePaymentForm from './intake/SquarePaymentForm';
import AccountSetupCta from './intake/AccountSetupCta';
import DexaCreditsNote from './intake/DexaCreditsNote';
import { DexaUpsell, AddOnLineItems } from './intake/CheckoutUpsells';
import {
  DEXA_LAB_PANEL_OFFER,
  DEXA_LAB_PANEL_TIERS,
  DEXA_LOCATION,
  labPanelDexaAvailable,
  dexaAddOnCents,
  dexaAddOnPayload,
} from '../data/dexaAddOn';
import { RecommendationGate, BenefitList, TrustBanner } from './intake/checkoutUi';
import BrandLogo from '@/components/BrandLogo';
import { buildTenantUrl } from '@/lib/tenant';
import {
  labPanelFlow,
  isDisqualifyingAnswer,
  getLabPanelTier,
  getCategoriesForSex,
  getTestTypesForSex,
} from '../data/labPanelData';
import { apiService } from '../services/api';
import { describeSubmitFailure } from '../lib/utils';
import { MutatingDots } from 'react-loader-spinner';

// 60 / 125 / 160-biomarker blood panel order form → Beluga
// labTestingAutoAuthorization (immediate approval). See
// docs/BACKEND_LAB_AUTOAUTH_VISIT_PRD.md.
const LabAuthorizationForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState(initialPanelAnswers);
  // §authenticated-patient handoff: prefill contact from the resolved ?pt= patient.
  useHandoffSeed(setAnswers);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [slideKey, setSlideKey] = useState(0);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualifyReason, setDisqualifyReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [visitResult, setVisitResult] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const submittingRef = useRef(false);

  const preview = usePreview();
  // §lab-panel-dexa: the DEXA step exists only for New York addresses. The
  // address step comes first, so dropping it never shifts an earlier index.
  const dexaOffered = labPanelDexaAvailable(answers['shipping-address']?.state);
  const flow = preview.wrapFlow(
    skipHandoffContact(dexaOffered ? labPanelFlow : labPanelFlow.filter((s) => s.id !== 'dexa-upsell')),
  );
  const totalSteps = flow.length;
  const currentQuestion = flow[currentStep];
  const tier = getLabPanelTier(answers['panel-overview']);
  // Sessions the patient added (1) or null; ignored unless still offered, so a
  // patient who switches to a non-NY address is never charged for it.
  const dexaSessions = dexaOffered ? answers['dexa-upsell'] || null : null;
  const dexaCents = dexaAddOnCents(dexaSessions, DEXA_LAB_PANEL_OFFER);
  // BE #382 backstop: the lab-panel DEXA is refused (dexa_invalid) unless the
  // add-on carries a New York state.
  const dexaBase = dexaAddOnPayload(dexaSessions, DEXA_LAB_PANEL_OFFER);
  const dexaPayload = dexaBase
    ? { ...dexaBase, state: String(answers['shipping-address']?.state || '').toUpperCase() }
    : null;

  const stagingTest =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('belugaEnv') === 'staging';

  const handleDisqualify = useCallback((reason) => {
    setIsDisqualified(true);
    setDisqualifyReason(reason);
    setSlideKey((p) => p + 1);
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setSlideKey((p) => p + 1);
      setCurrentStep((p) => p + 1);
    }
  }, [currentStep, totalSteps]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setSlideKey((p) => p + 1);
      setCurrentStep((p) => p - 1);
    }
  }, [currentStep]);

  const handleAnswer = useCallback(
    (questionId, answer) => {
      setAnswers((prev) => ({ ...prev, [questionId]: answer }));
      const dq = isDisqualifyingAnswer(questionId, answer);
      if (dq.disqualified) {
        handleDisqualify(dq.reason);
        return;
      }
      if (currentQuestion?.type === 'single-select') {
        setTimeout(() => handleNext(), 200);
      }
    },
    [currentQuestion, handleNext, handleDisqualify],
  );

  const handleStartOver = useCallback(() => {
    setCurrentStep(0);
    setAnswers(initialPanelAnswers());
    setIsComplete(false);
    setIsDisqualified(false);
    setDisqualifyReason('');
    setSlideKey((p) => p + 1);
  }, []);

  const submitOrder = useCallback(
    async (payment) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        setIsSubmitting(true);
        const contact = answers['basic-contact'] || {};
        const shipping = answers['shipping-address'] || {};
        const sexRaw = answers['sex-at-birth'];
        const sex = sexRaw ? sexRaw.charAt(0).toUpperCase() + sexRaw.slice(1) : '';
        const paymentId =
          payment?.paymentId ||
          payment?.payment_id ||
          localStorage.getItem('fitpal_intake_payment_id') ||
          '';

        const formObj = {
          firstName: contact.name?.split(' ')[0] || '',
          lastName: contact.name?.split(' ').slice(1).join(' ') || '',
          dob: contact.dob || '',
          phone: (contact.phone || '').replace(/\D/g, ''),
          email: contact.email || '',
          address: shipping.address1 || '',
          city: shipping.city || '',
          state: shipping.state || '',
          zip: shipping.zipCode || '',
          sex,
          testTypes: getTestTypesForSex(answers['sex-at-birth'], answers['panel-overview']),
        };

        const result = await apiService.submitLabAuthorization({ formObj, paymentId });

        // Relay the required government-ID photo (Beluga) post-submit.
        const idPhoto = answers['identity-verification'];
        if (idPhoto && idPhoto.preview && result?.masterId) {
          apiService
            .uploadVisitPhoto({
              masterId: result.masterId,
              visitId: result.visitId,
              slideId: 'identity-verification',
              photo: idPhoto,
            })
            .catch((err) => {
              console.warn('[uploadVisitPhoto] lab identity relay failed:', err?.response?.data || err.message);
            });
        }

        setVisitResult(result);
        setPaymentResult(payment);

        // §embed-bridge: tell the dashboard what was created. Waits for any

        // in-flight photo relay first — the parent closes its modal on this

        // message, which unmounts the iframe and would abort the upload.

        emitVisitCompleteWhenPhotosSettle({

          masterId: result?.masterId,

          visitId: result?.visitId,

          scheduleLink: result?.scheduleLink,

        });
        setIsComplete(true);
        handleNext();
      } catch (error) {
        console.error('Lab authorization submission error:', error);
        // §embed-bridge: relay the RAW backend error — the dashboard reads
        // errorCode/refundId to tell the patient their card was refunded.
        emitVisitError(error);
        alert(describeSubmitFailure(error));
      } finally {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [answers, handleNext],
  );

  // §lab-requisition: labTestingAutoAuthorization issues no requisition
  // document in any state — the visit-creation success response is the
  // approval (Natasha, Beluga, 2026-08-14). Do not promise the patient a slip.
  const requisitionNeed = resolveRequisitionNeed('labTestingAutoAuthorization');

  const renderStep = () => {
    if (isDisqualified) {
      return (
        <div key={slideKey} className="slide-wrapper slide-forward">
          <DisqualifiedStep reason={disqualifyReason} onStartOver={handleStartOver} />
        </div>
      );
    }
    if (!currentQuestion) return null;

    const commonProps = {
      question: preview.withIcons(currentQuestion),
      value: answers[currentQuestion.id],
      onAnswer: (a) => handleAnswer(currentQuestion.id, a),
      onNext: handleNext,
      onBack: handleBack,
      canGoBack: currentStep > 0,
      isLastStep: currentStep === totalSteps - 1,
      setLoading,
    };

    const content = (() => {
      switch (currentQuestion.type) {
        case 'lab-panel-overview':
          return (
            <LabPanelOverview
              question={currentQuestion}
              onNext={handleNext}
              sex={answers['sex-at-birth']}
              tierId={tier.id}
              onSelectTier={(id) => setAnswers((prev) => ({ ...prev, 'panel-overview': id }))}
            />
          );
        case 'single-select':
          return <QuestionStep {...commonProps} />;
        case 'basic-contact':
          return <BasicContactStep {...commonProps} />;
        case 'shipping-address':
          return <ShippingAddressStep {...commonProps} />;
        case 'file-upload':
          return <FileUploadStep {...commonProps} />;
        case 'consent-dropdown':
          return <ConsentDropdownStep {...commonProps} onDisqualify={handleDisqualify} />;
        case 'lab-dexa-upsell':
          return (
            <div className="max-w-md mx-auto" data-testid="lab-dexa-step">
              <DexaUpsell
                offer={DEXA_LAB_PANEL_OFFER}
                tiers={DEXA_LAB_PANEL_TIERS}
                sessions={answers['dexa-upsell'] || null}
                setSessions={(n) => setAnswers((prev) => ({ ...prev, 'dexa-upsell': n }))}
                title="Add a DEXA body composition scan"
                body={`See fat, muscle and bone separately to go with your bloodwork. About 15 minutes at ${DEXA_LOCATION}.`}
                singleNote="New York only · book anytime from your dashboard"
                continueLabel="Continue to checkout"
                onContinue={handleNext}
                onBack={handleBack}
              />
            </div>
          );
        case 'checkout':
          return (
            <RecommendationGate
              product={{
                name: tier.name,
                sub: `${tier.biomarkers}-biomarker panel (sex-specific)`,
                price: `$${tier.price}`,
              }}
              headline="Your lab panel is ready to order."
              subhead="Confirm your comprehensive blood panel below."
              aboutItems={[
                'Reviewed & authorized by a licensed provider',
                'Visit any Quest or LabCorp draw site',
                'Results delivered to your dashboard',
              ]}
            >
              <div data-testid="lab-checkout-step" className="max-w-md mx-auto">
                <div className="mb-5">
                  <p className="text-[#2d8a5f] font-semibold text-sm">You're in!</p>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">Last step before your results.</h2>
                </div>

                {/* Panel summary card */}
                <div className="rounded-3xl border border-[#2d8a5f]/30 bg-[#f0faf4] p-5 mb-3">
                  <div className="flex items-center gap-4 pb-4 border-b border-[#2d8a5f]/15">
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-gray-900 leading-snug">{tier.name}</p>
                      <p className="text-sm text-gray-500">
                        {tier.highlights
                          ? `${tier.tierLabel} panel · sex-specific`
                          : `${getCategoriesForSex(answers['sex-at-birth']).length} biomarker categories · sex-specific`}
                      </p>
                    </div>
                    <span className="text-2xl font-bold text-[#2d8a5f] flex-shrink-0">${tier.price}</span>
                  </div>
                  {dexaCents > 0 && (
                    <div className="pt-3 border-b border-[#2d8a5f]/15">
                      <AddOnLineItems dexaSessions={dexaSessions} dexaCents={dexaCents} conciergeCents={0} />
                      <div className="flex justify-between font-semibold text-gray-900 mb-3" data-testid="lab-total">
                        <span>Total</span>
                        <span>${(tier.price * 100 + dexaCents) / 100}</span>
                      </div>
                    </div>
                  )}
                  <BenefitList
                    className="pt-4"
                    items={[
                      'Provider-authorized lab order',
                      `${tier.biomarkers}+ biomarkers, sex-specific`,
                      'Results in your dashboard',
                    ]}
                  />
                </div>

                {/* Trust banner */}
                <TrustBanner>
                  <span className="font-semibold">No clinic visit fees</span> — we send your order to the lab and email your draw-site details.
                </TrustBanner>

                {/* Payment */}
                <div className="rounded-2xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Payment details</h3>
                  {stagingTest ? (
                    <div className="space-y-3">
                      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        ⚠️ Staging sign-off mode — Square is bypassed. This submits a test lab
                        order to Beluga <strong>staging</strong> with no charge.
                      </p>
                      <button
                        type="button"
                        data-testid="staging-bypass-pay"
                        disabled={isSubmitting}
                        onClick={() => submitOrder({ paymentId: 'STAGING_TEST_BYPASS' })}
                        className="w-full rounded-xl bg-[#2d8a5f] px-4 py-3 font-semibold text-white disabled:opacity-50"
                      >
                        {isSubmitting ? 'Submitting…' : '[Staging test] Skip payment & submit to Beluga staging'}
                      </button>
                    </div>
                  ) : (
                    <SquarePaymentForm
                      key={`${tier.planId}-${dexaCents}`}
                      planId={tier.planId}
                      amount={tier.price * 100 + dexaCents}
                      dexaAddOn={dexaPayload}
                      nonDiscountableCents={dexaCents}
                      onComplete={submitOrder}
                      answers={answers}
                      setLoading={setLoading}
                      clearSavedProgress={() => {}}
                      primaryGoal="labTestingAutoAuthorization"
                      currentStep={currentStep}
                      timestamp={Date.now()}
                    />
                  )}
                </div>
              </div>
            </RecommendationGate>
          );
        case 'complete':
          return (
            <div className="max-w-xl mx-auto text-center py-10" data-testid="lab-complete-step">
              <div className="w-16 h-16 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-5">
                <svg className="w-8 h-8 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Thanks{answers['basic-contact']?.name ? `, ${answers['basic-contact'].name.split(' ')[0]}` : ''}!
              </h2>
              <p className="text-gray-500 mb-4">
                {requisitionCompletionCopy(requisitionNeed)}
              </p>
              {visitResult?.masterId && (
                <div className="inline-flex flex-col items-center bg-gray-50 rounded-xl px-6 py-3">
                  <span className="text-xs uppercase tracking-wide text-gray-400">Order reference</span>
                  <span className="text-lg font-semibold text-gray-900" data-testid="lab-visit-ref">
                    {visitResult.masterId}
                  </span>
                </div>
              )}
              <DexaCreditsNote paymentResult={paymentResult} />
              <AccountSetupCta email={answers['basic-contact']?.email} />
            </div>
          );
        case 'preview-interstitial':
          return <PreviewInterstitial step={currentQuestion} onNext={handleNext} />;
        default:
          return null;
      }
    })();

    return (
      <div key={slideKey} className="slide-wrapper slide-forward">
        {preview.frame(currentQuestion, content, handleBack)}
      </div>
    );
  };

  return (
    <>
      <div className="intake-form-loader" style={{ display: loading ? '' : 'none' }}>
        <MutatingDots />
      </div>
      <div
        className={`min-h-screen flex flex-col ${preview.rootClass}`}
        data-testid="lab-form"
        style={{ display: loading === false ? '' : 'none' }}
      >
        <header className="w-full py-4 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            {currentStep > 0 && !isComplete && !isDisqualified ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
                data-testid="back-button"
                style={{ display: currentQuestion?.type === 'complete' ? 'none' : '' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">Back</span>
              </button>
            ) : (
              <a href={buildTenantUrl('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0" data-testid="home-link">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">Home</span>
              </a>
            )}
            <div className="text-center flex-1">
              <BrandLogo alt="FitPal" className="mx-auto" style={{ width: '400px', height: 'auto', maxWidth: '100%' }} />
              <p className="text-xs text-gray-500 mt-1">Comprehensive Lab Testing</p>
            </div>
            <div className="w-16 flex-shrink-0" />
          </div>
        </header>

        {!isComplete && !isDisqualified && (
          <div className="w-full px-4 py-2">
            <div className="max-w-2xl mx-auto">
              {preview.progress(currentQuestion, currentStep, totalSteps)}
            </div>
          </div>
        )}

        {!isComplete && !isDisqualified && <LabFlowIntent flow="panel" />}

        <main className="flex-1 flex flex-col items-center justify-start px-4 py-8">
          <div className="w-full max-w-2xl slide-container">{renderStep()}</div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default LabAuthorizationForm;
